import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

interface SearchConfig {
  enabled: boolean;
  disabledMessage: string;
}

interface HeroSectionProps {
  searchConfig?: SearchConfig;
}

export default function HeroSection({ searchConfig }: HeroSectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const navigate = useNavigate();

  const searchEnabled = searchConfig?.enabled !== false;
  const disabledMessage = searchConfig?.disabledMessage || "搜索功能已关闭";

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchEnabled) {
      setShowTip(true);
      setTimeout(() => setShowTip(false), 3000);
      return;
    }
    
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <section className="relative min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20warm%20gradient%20background%20soft%20orange%20peach%20tones%20minimal%20modern%20business%20professional%20smooth%20texture&width=1920&height=800&seq=hero-bg&orientation=landscape"
          alt="background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 md:px-8 pt-20 pb-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className={`text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 transition-all duration-700 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            专业商业服务
            <br />
            <span className="text-orange-400">信息平台</span>
          </h1>
          <p 
            className={`text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-150 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            连接优质服务提供商与企业客户，让商业合作更简单高效
          </p>

          {/* Search Box */}
          <form
            onSubmit={handleSearch}
            className={`max-w-2xl mx-auto mb-6 transition-all duration-700 delay-300 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 relative">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="请输入关键词"
                  className="w-full pl-11 pr-4 py-3.5 md:py-4 bg-white rounded-xl sm:rounded-r-none text-sm md:text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              <button
                type="submit"
                className="px-6 md:px-8 py-3.5 md:py-4 text-white font-medium rounded-xl sm:rounded-l-none bg-[#8B4513] hover:bg-[#6B3410] transition-colors text-sm md:text-base whitespace-nowrap"
              >
                搜索
              </button>
            </div>
            
            {/* 搜索关闭提示 */}
            {showTip && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-4 py-2 bg-orange-500 text-white text-sm rounded-lg shadow-lg whitespace-nowrap">
                <i className="ri-error-warning-line mr-1" />
                {disabledMessage}
              </div>
            )}
          </form>

          {/* CTA Buttons */}
          <div 
            className={`flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 transition-all duration-700 delay-600 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <button
              onClick={() => navigate("/publish")}
              className="px-6 py-3 bg-orange-500 text-white font-medium rounded-full hover:bg-orange-600 transition-all duration-300 text-sm whitespace-nowrap hover:scale-105 hover:shadow-lg"
            >
              <i className="ri-add-line mr-1" />
              立即发布服务
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
